import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, MessageSquare, Wrench, BookOpen, ArrowRight, Sparkles, Quote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

const HERO_IMG = "https://images.unsplash.com/photo-1620500152111-2c3c8efd6f81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxjb25maWRlbnQlMjB3ZWxsLWRyZXNzZWQlMjBtYW4lMjBwb3J0cmFpdCUyMGRhcmt8ZW58MHx8fHwxNzgzNjU2ODM3fDA&ixlib=rb-4.1.0&q=85";
const IMG_2 = "https://images.unsplash.com/photo-1748551204300-f227d5af350f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3VwbGUlMjByb21hbnRpYyUyMGRpbm5lciUyMGRhcmslMjBhbWJpZW50fGVufDB8fHx8MTc4MzY1NjgzN3ww&ixlib=rb-4.1.0&q=85";
const IMG_3 = "https://images.unsplash.com/photo-1780337092500-f59e1366e629?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwbGlmZXN0eWxlJTIwbWVuJTIwZGFya3xlbnwwfHx8fDE3ODM2NTY4Mzd8MA&ixlib=rb-4.1.0&q=85";

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  return (
    <div className="min-h-screen grain">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 glass-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <Flame className="w-5 h-5 text-amber-500" />
            <span className="font-serif text-2xl">Suitor</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-400">
            <a href="#pillars" className="hover:text-white transition-colors">The Playbook</a>
            <a href="#coach" className="hover:text-white transition-colors">AI Coach</a>
            <a href="#tools" className="hover:text-white transition-colors">Tools</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-neutral-300 hover:text-white" data-testid="landing-login-link">Sign in</Link>
            <Link to="/login" className="btn-amber text-sm py-2 px-4" data-testid="landing-cta-nav">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-7"
          >
            <div className="overline mb-4" data-testid="hero-overline">A MEN'S DATING PLAYBOOK</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white mb-6">
              Become the man <br />
              <span className="italic text-amber-500">she chooses.</span>
            </h1>
            <p className="text-lg text-neutral-400 max-w-xl leading-relaxed mb-8">
              Approach, texting, ghosting, first dates, long-term love — a straight-talking coach in your pocket.
              No pickup tricks. Just clarity, confidence, and the skills to build something real.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/login" className="btn-amber inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Start free <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#pillars" className="btn-ghost" data-testid="hero-cta-secondary">See the playbook</a>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-neutral-500">
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Personalised AI coaching</div>
              <div className="hidden sm:block">·</div>
              <div>100% free during launch</div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-5"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]">
              <img src={HERO_IMG} alt="Confidence" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <Quote className="w-6 h-6 text-amber-500 mb-2" />
                <p className="font-serif text-2xl text-white leading-snug">
                  "You will never feel your way into confidence. You act, gather evidence, and belief follows."
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section id="pillars" className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-14">
            <div className="overline mb-3">THE PLAYBOOK</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-tight">
              Every skill she wishes you had — <span className="italic text-neutral-400">in one place.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Approach", desc: "Walk up without freezing. Open with intent, exit with grace." },
              { title: "Texting & Reply", desc: "Land every message. Never sound needy. Move to plans faster." },
              { title: "Ghosting", desc: "Read the silence honestly. Reset your energy. Move forward clean." },
              { title: "First Dates", desc: "Lead without dominating. Kiss at the high point. Follow up right." },
              { title: "Long-Term Love", desc: "Novelty + reliability. Argue clean. Keep growing together." },
              { title: "Mindset", desc: "Inner game beats every line. Confidence is a receipt from action." },
            ].map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="card-surface p-8 hover-lift"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s|&/g, "-")}`}
              >
                <div className="overline mb-3 text-neutral-500">CHAPTER 0{i+1}</div>
                <h3 className="font-serif text-2xl mb-3 text-white">{p.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coach */}
      <section id="coach" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={IMG_2} alt="Romance" className="w-full h-[440px] object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="overline mb-3">YOUR AI COACH</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              A wise older brother, <span className="italic text-amber-500">always awake.</span>
            </h2>
            <p className="text-neutral-400 text-lg mb-8 leading-relaxed">
              Ask anything — how to respond to a cold text, whether she's really interested, how to lead a first date.
              Suitor gives you direct, personalised, no-BS coaching backed by GPT-5.2.
            </p>
            <div className="space-y-3">
              {["Personalised chat coaching", "Text reply generator", "Situation analyzer with next moves", "Opener suggestions by context"].map(x=>(
                <div key={x} className="flex items-center gap-3 text-neutral-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"/>{x}
                </div>
              ))}
            </div>
            <Link to="/login" className="btn-amber inline-flex items-center gap-2 mt-8" data-testid="coach-cta">
              Start with a question <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section id="tools" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-14 max-w-3xl">
            <div className="overline mb-3">INTERACTIVE TOOLS</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Real situations. <span className="italic text-neutral-400">Real answers.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-5 card-surface p-8 hover-lift">
              <MessageSquare className="w-8 h-8 text-amber-500 mb-4"/>
              <h3 className="font-serif text-2xl mb-2">Text Reply Generator</h3>
              <p className="text-neutral-400">Paste what she said. Get 3 replies + why they work.</p>
            </div>
            <div className="md:col-span-7 card-surface p-8 hover-lift relative overflow-hidden">
              <img src={IMG_3} alt="lifestyle" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              <div className="relative">
                <Wrench className="w-8 h-8 text-amber-500 mb-4"/>
                <h3 className="font-serif text-2xl mb-2">Opener Generator</h3>
                <p className="text-neutral-300">Charismatic openers by situation and platform.</p>
              </div>
            </div>
            <div className="md:col-span-12 card-surface p-8 hover-lift">
              <BookOpen className="w-8 h-8 text-amber-500 mb-4"/>
              <h3 className="font-serif text-2xl mb-2">Situation Analyzer</h3>
              <p className="text-neutral-400 max-w-2xl">Describe the moment. Get a clear read, signals to watch, next move, and a mindset reframe — in under 15 seconds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-serif text-4xl sm:text-6xl leading-tight mb-6">
            The next great chapter of your dating life<br/>
            <span className="italic text-amber-500">starts tonight.</span>
          </h2>
          <p className="text-neutral-400 text-lg mb-8">Free during launch. No credit card. Sign in and start.</p>
          <Link to="/login" className="btn-amber inline-flex items-center gap-2" data-testid="footer-cta">
            Create your account <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-neutral-500">
            <Flame className="w-4 h-4 text-amber-500" />
            <span className="font-serif text-lg text-white">Suitor</span>
            <span className="text-xs">© 2026</span>
          </div>
          <p className="text-xs text-neutral-500">Built with respect. Coaching men to be their best.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
